import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { PlusIcon } from '@phosphor-icons/react'

import { Button, Divider, ToastContext } from '@reveni-io/reveni-ui'

import LoadMore from '@/common/components/LoadMore/LoadMore'
import QueryFilter from '@/common/components/QueryFilter/QueryFilter'
import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useDragSort from '@/common/hooks/useDragSort/useDragSort'
import useShowMore from '@/common/hooks/useShowMore/useShowMore'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import AvailabilityCriterionItemList from './AvailabilityCriterionItemList/AvailabilityCriterionItemList'
import DeleteAvailabilityCriterionModal from './DeleteAvailabilityCriterionModal/DeleteAvailabilityCriterionModal'
import useAvailabilityCriteria from './hooks/useAvailabilityCriteria/useAvailabilityCriteria'
import * as Styles from './styles'

const AvailabilityCriteriaTab = ({
  getAvailabilityCriteria,
  reorderAvailabilityCriteria,
  deleteAvailabilityCriterion,
  detailRoute,
}) => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()

  const {
    availabilityCriteria,
    gettingAvailabilityCriteria,
    search,
    setSearch,
    loadMore,
    hasNextPage,
    totalCount,
    navigateToDetail,
    navigateToDuplicate,
    navigateToCreate,
    reorderCriteria,
    hasChanges,
    revertChanges,
    onSave,
    reorderingAvailabilityCriteria,
    deletingAvailabilityCriterion,
    criterionToDelete,
    setCriterionToDelete,
    confirmDeleteCriterion,
    cancelDeleteCriterion,
  } = useAvailabilityCriteria({
    storeId: info?.selectedStore?.id,
    setToastInfo,
    getAvailabilityCriteria,
    reorderAvailabilityCriteria,
    deleteAvailabilityCriterion,
    detailRoute,
  })

  const { elementsToShow, hasMoreElements, showMore, totalElements, numberOfElementsToShow } = useShowMore({
    elements: availabilityCriteria,
    paginated: true,
    hasNextPage,
    onLoadMore: loadMore,
    totalCount,
  })

  const { dragging, onSortStart, onSortEnd } = useDragSort(reorderCriteria)
  const isEditable = hasPermission(userPermissions.editLogistics)
  const isEmpty = !gettingAvailabilityCriteria && totalCount === 0 && !search

  return (
    <Styles.MainContainer>
      {hasChanges && <UnsavedChangesAlert hasPermission={isEditable} />}
      {!isEmpty && (
        <>
          <QueryFilter onChange={setSearch} placeholder={t('Id, Nombre')} />
          <Divider />
        </>
      )}
      <AvailabilityCriterionItemList
        criteria={elementsToShow}
        loading={gettingAvailabilityCriteria}
        onEdit={navigateToDetail}
        onDuplicate={navigateToDuplicate}
        onDelete={setCriterionToDelete}
        dragging={dragging}
        onSortStart={onSortStart}
        onSortEnd={onSortEnd}
      />
      {!isEmpty && (
        <>
          <Divider />
          <LoadMore
            numberOfElementsToShow={numberOfElementsToShow}
            totalElements={totalElements}
            hasMoreElements={hasMoreElements}
            showMore={showMore}
          />
        </>
      )}
      {isEditable && (
        <div>
          <Button variant="text" size="s" onClick={navigateToCreate}>
            <PlusIcon size={24} />
            {t('Añadir nuevos criterios de disponibilidad')}
          </Button>
        </div>
      )}
      <SaveChangesButtons
        hasChanges={isEditable && hasChanges}
        onSave={onSave}
        onRevertChanges={revertChanges}
        loading={reorderingAvailabilityCriteria}
        disabled={reorderingAvailabilityCriteria}
      />
      <DeleteAvailabilityCriterionModal
        show={!!criterionToDelete}
        onClose={cancelDeleteCriterion}
        onDelete={confirmDeleteCriterion}
        loading={deletingAvailabilityCriterion}
      />
    </Styles.MainContainer>
  )
}

export default AvailabilityCriteriaTab
